import React from 'react';

import {
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import {Moment} from 'moment';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';

import {
  EAnalyticsDateRange,
  ReportSectionId,
  ReportSectionOption,
} from './analyticsTypes';

type AnalyticsReportDrawerProps = {
  open: boolean;
  onClose: () => void;
  dateRangeId: EAnalyticsDateRange;
  startDate: Moment | null | undefined;
  endDate: Moment | null | undefined;
  onDateRangeChange: (dateRange: EAnalyticsDateRange) => void;
  onStartDateChange: (date: Moment | null) => void;
  onEndDateChange: (date: Moment | null) => void;
  reportSectionOptions: ReportSectionOption[];
  selectedReportSections: ReportSectionId[];
  onToggleSection: (sectionId: ReportSectionId) => void;
  rangeLabel: string;
  loading: boolean;
  generating: boolean;
  canGenerate: boolean;
  onGenerate: () => void;
};

export function AnalyticsReportDrawer(props: AnalyticsReportDrawerProps) {
  const handleDateRangeChange = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    props.onDateRangeChange(event.target.value as EAnalyticsDateRange);
  };

  return (
    <Drawer
      anchor="right"
      open={props.open}
      onClose={props.onClose}
      classes={{paper: 'analytics-report-drawer'}}
    >
      <div className="analytics-report-drawer-header">
        <div>
          <Typography className="analytics-report-drawer-title">
            Export analytics report
          </Typography>
          <Typography className="analytics-report-drawer-subtitle">
            Choose the date range and sections to include in the PDF report.
          </Typography>
        </div>
        <IconButton
          size="small"
          onClick={props.onClose}
          aria-label="Close report export"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="analytics-report-drawer-body">
        <Typography className="analytics-report-section-label">
          Date range
        </Typography>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl
                className="analytics-filter-control analytics-report-date-control"
                variant="outlined"
                fullWidth
              >
                <InputLabel id="report-date-range-select-label">
                  Date Range
                </InputLabel>
                <Select
                  labelId="report-date-range-select-label"
                  id="report-date-range-select"
                  label="Date Range"
                  value={props.dateRangeId}
                  onChange={handleDateRangeChange}
                  fullWidth
                >
                  <MenuItem value={EAnalyticsDateRange.NONE}>
                    Select Date Range
                  </MenuItem>
                  <MenuItem value={EAnalyticsDateRange.CUSTOM}>
                    Custom Date Range
                  </MenuItem>
                  <MenuItem value={EAnalyticsDateRange.TODAY}>Today</MenuItem>
                  <MenuItem value={EAnalyticsDateRange.THIS_WEEK}>
                    This Week
                  </MenuItem>
                  <MenuItem value={EAnalyticsDateRange.THIS_MONTH}>
                    This Month
                  </MenuItem>
                  <MenuItem value={EAnalyticsDateRange.THIS_YEAR}>
                    This Year
                  </MenuItem>
                  <MenuItem value={EAnalyticsDateRange.LAST_7_DAYS}>
                    Last 7 Days
                  </MenuItem>
                  <MenuItem value={EAnalyticsDateRange.LAST_30_DAYS}>
                    Last 30 Days
                  </MenuItem>
                  <MenuItem value={EAnalyticsDateRange.ALL_TIME}>
                    All Time
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <KeyboardDatePicker
                autoOk
                variant="inline"
                inputVariant="outlined"
                label="Start Date"
                format="DD/MM/yyyy"
                value={props.startDate}
                onChange={props.onStartDateChange}
                className="analytics-filter-control analytics-report-date-control"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <KeyboardDatePicker
                autoOk
                variant="inline"
                inputVariant="outlined"
                label="End Date"
                format="DD/MM/yyyy"
                value={props.endDate}
                onChange={props.onEndDateChange}
                className="analytics-filter-control analytics-report-date-control"
                fullWidth
              />
            </Grid>
          </Grid>
        </MuiPickersUtilsProvider>

        <Typography className="analytics-report-section-label">
          Report sections
        </Typography>
        <div className="analytics-report-section-list">
          {props.reportSectionOptions.map((section) => (
            <FormControlLabel
              key={section.id}
              className="analytics-report-section-option"
              control={
                <Checkbox
                  color="primary"
                  checked={props.selectedReportSections.includes(section.id)}
                  onChange={() => props.onToggleSection(section.id)}
                />
              }
              label={
                <div>
                  <span>{section.label}</span>
                  <small>{section.description}</small>
                </div>
              }
            />
          ))}
        </div>

        <div className="analytics-report-preview-note">
          <strong>{props.rangeLabel}</strong>
          <span>
            {props.loading
              ? 'Refreshing analytics for this range...'
              : 'The report uses the currently loaded analytics data.'}
          </span>
        </div>
      </div>

      <div className="analytics-report-drawer-actions">
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant="contained"
          className="analytics-report-generate-button"
          startIcon={<GetAppIcon />}
          disabled={!props.canGenerate || props.generating}
          onClick={props.onGenerate}
        >
          {props.generating ? 'Saving...' : 'Save PDF'}
        </Button>
      </div>
    </Drawer>
  );
}
